import { useMemo } from 'react';
import { ImoduleFilter, WAREHOUSE_FILTER_ACTIVE_QUERY_PATH } from 'types/global';
import { DTOgetKamUserWithOrganization } from 'types/orders';
import {
  FILTER_NAME_TO_BACKEND_MAPPING,
  FILTER_NAMES,
  ETA_OVERDUE_VALUE,
  MISSING_OPTION_VALUES,
  PREPARATION_VALUE_TO_FIELD_ID_MAP,
  OTHERS_OPTION_VALUES,
  IFocusedFilterValuesMap,
  TRawFilterValue
} from '../OrdersTrackingOperationsFilters/filter.constants';
import {
  SEARCH_TYPE_TO_FILTER_CONFIG,
  ORDER_PROPERTY_TO_FILTER_KEYS,
  isStringArray,
  isNumberArray
} from '../../trackingOrder.utils';

interface ISearchTypeOption {
  name: string;
  propertyToFilter?: string;
}

interface IUseQueryParamsOptions {
  focusedFilterValues: IFocusedFilterValuesMap;
  focusedOrderId: number | null;
  ordersFilters: ImoduleFilter[];
  sortFilter: { value: string; propertyToSort?: string } | null;
  kamFiltersData?: DTOgetKamUserWithOrganization[];
  searchText?: string;
  propertyToFilterSearch?: ISearchTypeOption | null;
}

const buildFilterParam = (entity: string, field: string, operator: string, value: string | number): string => {
  const encodedValue = encodeURIComponent(String(value));
  return `filters[${entity}.${field}][$${operator}]=${encodedValue}`;
};

const appendEtaOverdueFilterParams = (params: string[]): void => {
  const today = new Date().toISOString().split('T')[0];

  params.push(buildFilterParam('shipment', 'estimatedDeliveryDate', 'lt', today));
  params.push(buildFilterParam('shipment', 'estimatedDeliveryDate', 'not', 'null'));
  params.push(buildFilterParam('warehouseTemporaryStorage', 'estimatedEntryDate', 'lt', today));
  params.push(buildFilterParam('warehouseTemporaryStorage', 'estimatedEntryDate', 'not', 'null'));
};

const buildFilterParamWithIn = (entity: string, field: string, values: (string | number)[]): string => {
  if (values.length === 0) return '';
  const encodedValues = values.map(v => encodeURIComponent(String(v))).join(',');
  return `filters[${entity}.${field}][$in]=${encodedValues}`;
};

/**
 * Construye los query params del filtro Otros (nuevo ingreso, cotización, parada y sin parada en bodega).
 * @param values - Valores internos seleccionados en el filtro Otros
 * @returns Lista de parámetros de filtro para la URL
 */
const buildOthersFilterParams = (values: string[]): string[] => {
  const params: string[] = [];
  const othersMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.OTHERS];

  if (values.includes(OTHERS_OPTION_VALUES.NEW_ENTRY) && othersMapping) {
    params.push(buildFilterParam(othersMapping.entity, othersMapping.field, 'not', 'null'));
  }

  if (values.includes(OTHERS_OPTION_VALUES.WAREHOUSE_STOP)) {
    params.push(buildFilterParam('shipment', 'hasWarehouseStop', 'eq', 'true'));
  }

  if (values.includes(OTHERS_OPTION_VALUES.NO_WAREHOUSE_STOP)) {
    params.push(buildFilterParam('shipment', 'hasWarehouseStop', 'eq', 'false'));
  }

  if (values.includes(OTHERS_OPTION_VALUES.QUOTATION)) {
    const quotationMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.QUOTATION];
    if (quotationMapping) {
      params.push(buildFilterParam(quotationMapping.entity, quotationMapping.field, 'not', 'null'));
    }
  }

  return params;
};

/**
 * Normaliza el valor enfocado de un filtro multi-select a un arreglo de strings.
 * @param value - Valor enfocado del filtro
 * @returns Arreglo de valores string
 */
const normalizeFocusedValuesToStringArray = (value: TRawFilterValue | null | undefined): string[] => {
  if (value == null) return [];
  if (isStringArray(value)) return value;
  if (typeof value === 'string' && value !== '') return [value];
  return [];
};

const normalizeFocusedKamIds = (value: TRawFilterValue | null | undefined): number[] => {
  if (value == null || !value) return [];
  if (isNumberArray(value)) return value;
  if (typeof value === 'number') return [value];
  return [];
};

const convertFilterToParams = (filter: ImoduleFilter): string[] => {
  const mapping = FILTER_NAME_TO_BACKEND_MAPPING[filter.name];
  if (!mapping && filter.name !== FILTER_NAMES.ORDER_TYPE) return [];

  if (filter.name === FILTER_NAMES.OTHERS) {
    const stringValues = filter.values.filter((v): v is string => typeof v === 'string');
    return buildOthersFilterParams(stringValues);
  }

  if (filter.name === FILTER_NAMES.ORDER_TYPE && filter.values?.length) {
    const stringValues = filter.values.filter((v): v is string => typeof v === 'string');
    if (stringValues.length === 0) return [];
    return [buildFilterParamWithIn('shipment', 'destinationPlace', stringValues)];
  }

  if (filter.name === FILTER_NAMES.SLA_ETA) {
    const etaOverdueValues = filter.values.filter(value => value === ETA_OVERDUE_VALUE);
    const slaStatusValues = filter.values.filter(value => value !== ETA_OVERDUE_VALUE);
    const params: string[] = [];

    if (etaOverdueValues.length > 0) {
      appendEtaOverdueFilterParams(params);
    }

    if (slaStatusValues.length > 0) {
      params.push(...slaStatusValues.map(value => buildFilterParam(mapping.entity, mapping.field, 'eq', value)));
      params.push(buildFilterParam(mapping.entity, mapping.field, 'not', 'null'));
    }

    return params;
  }

  if (filter.name === FILTER_NAMES.CONFIGURATION && filter.values?.length) {
    return [buildFilterParamWithIn(mapping.entity, mapping.field, filter.values)];
  }

  const operator = filter.includes === 'in' ? 'eq' : filter.includes || 'eq';
  return filter.values.map(value => buildFilterParam(mapping.entity, mapping.field, operator, value));
};

const buildFocusQueryParams = (
  focusedFilterValues: IFocusedFilterValuesMap,
  focusedOrderId: number | null,
  ordersFilters: ImoduleFilter[],
  kamFiltersData?: DTOgetKamUserWithOrganization[],
  searchText?: string,
  propertyToFilterSearch?: ISearchTypeOption | null
): string => {
  const params: string[] = [];

  const trimmedSearchText = searchText?.trim();
  const searchTypeKey = propertyToFilterSearch?.propertyToFilter;
  const searchFilterConfig = searchTypeKey ? SEARCH_TYPE_TO_FILTER_CONFIG[searchTypeKey] : null;
  const shouldAddSearchTextAsFilter =
    !!trimmedSearchText && !!searchFilterConfig && searchTypeKey !== ORDER_PROPERTY_TO_FILTER_KEYS.ORDER_NUMBER;

  if (shouldAddSearchTextAsFilter && searchFilterConfig) {
    const [entity, field] = searchFilterConfig.name.split('.');
    const operator = searchFilterConfig.includes.replace('$', '');
    params.push(buildFilterParam(entity, field, operator, trimmedSearchText));
  }

  const paymentType = focusedFilterValues[FILTER_NAMES.PAYMENT_TYPE];
  const configuration = focusedFilterValues[FILTER_NAMES.CONFIGURATION];
  const orderType = focusedFilterValues[FILTER_NAMES.ORDER_TYPE];

  const paymentMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.PAYMENT_TYPE];
  if (paymentMapping && paymentType != null) {
    if (Array.isArray(paymentType) && paymentType.length > 0) {
      params.push(buildFilterParamWithIn(paymentMapping.entity, paymentMapping.field, paymentType));
    } else if (typeof paymentType === 'string' && paymentType !== '') {
      params.push(buildFilterParam(paymentMapping.entity, paymentMapping.field, 'eq', paymentType));
    }
  }

  const companyFilter = ordersFilters.find(f => f.name === FILTER_NAMES.COMPANY);
  if (companyFilter?.values?.length) {
    const organizationIds = companyFilter.values.filter((v): v is string => typeof v === 'string');
    if (organizationIds.length > 0) {
      params.push(buildFilterParamWithIn('order', 'organizationId', organizationIds));
    }
  }

  const configValues =
    configuration == null
      ? []
      : isStringArray(configuration as TRawFilterValue)
      ? (configuration as string[])
      : [String(configuration)];
  if (configValues.length > 0) {
    params.push(buildFilterParamWithIn('productConfig', 'status', configValues));
  }

  const orderTypeValues = normalizeFocusedValuesToStringArray(orderType as TRawFilterValue);
  if (orderTypeValues.length > 0) {
    params.push(buildFilterParamWithIn('shipment', 'destinationPlace', orderTypeValues));
  }

  const focusedKamIds = normalizeFocusedKamIds(focusedFilterValues[FILTER_NAMES.KAM]);
  if (focusedKamIds.length > 0 && kamFiltersData) {
    const kamMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.KAM];
    const organizationIds = focusedKamIds
      .flatMap(kamId => {
        const kamData = kamFiltersData?.find(kam => String(kam?.id) === String(kamId));
        return kamData?.organizationIds ?? [];
      })
      .filter((orgId, index, ids) => ids.indexOf(orgId) === index);
    if (organizationIds?.length > 0 && kamMapping) {
      params.push(buildFilterParamWithIn(kamMapping?.entity, kamMapping?.field, organizationIds));
    }
  }

  if (focusedOrderId) {
    params.push(buildFilterParam('order', 'id', 'eq', focusedOrderId));
  }

  const focusedResponsibleId = focusedFilterValues[FILTER_NAMES.RESPONSIBLE];
  if (typeof focusedResponsibleId === 'number') {
    const responsibleMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.RESPONSIBLE];
    if (responsibleMapping) {
      params.push(buildFilterParam(responsibleMapping.entity, responsibleMapping.field, 'eq', focusedResponsibleId));
    }
  }

  const countryFilter = ordersFilters.find(f => f.name === FILTER_NAMES.COUNTRY_DESTINATION);
  const countryMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.COUNTRY_DESTINATION];
  if (countryMapping && countryFilter?.values?.length) {
    const countryNames = countryFilter.values.filter((v): v is string => typeof v === 'string');
    if (countryNames.length > 0) {
      params.push(buildFilterParamWithIn(countryMapping.entity, countryMapping.field, countryNames));
    }
  }

  const monthFilter = ordersFilters.find(f => f.name === FILTER_NAMES.MONTH);
  if (monthFilter?.values?.length) {
    const ranges = monthFilter.values
      .filter((v): v is string => typeof v === 'string' && v.includes(','))
      .map(v => {
        const [start, end] = v.split(',');
        return { start: start ?? '', end: end ?? '' };
      })
      .filter(r => r.start && r.end);

    if (ranges.length > 0) {
      const minStart = ranges.map(r => r.start).sort()[0];
      const maxEnd = ranges
        .map(r => r.end)
        .sort()
        .reverse()[0];
      params.push(buildFilterParam('shipment', 'createdAt', 'between', `${minStart},${maxEnd}`));
    }
  }

  const MISSING_FILTER_MAP: Record<string, [string, string, string, string]> = {
    [MISSING_OPTION_VALUES.WITHOUT_SERIAL]: ['sale', 'serial', 'eq', 'null'],
    [MISSING_OPTION_VALUES.WITHOUT_COST]: ['saleInfo', 'purchaseValue', 'is', 'null'],
    [MISSING_OPTION_VALUES.WITHOUT_PROVIDER]: ['saleInfo', 'providerName', 'is', 'null'],
    [MISSING_OPTION_VALUES.WITHOUT_PO]: ['saleInfo', 'providerPurchaseOrder', 'is', 'null'],
    [MISSING_OPTION_VALUES.WITHOUT_MPN]: ['product', 'mpnId', 'is', 'null']
  };

  const focusedMissing = focusedFilterValues[FILTER_NAMES.MISSING];
  const missingFilterArgs = MISSING_FILTER_MAP[focusedMissing as string];

  if (missingFilterArgs) {
    params.push(buildFilterParam(...missingFilterArgs));
  }

  const focusedHasInvoice = focusedFilterValues[FILTER_NAMES.HAS_INVOICE];
  if (focusedHasInvoice === true || focusedHasInvoice === false) {
    const invoiceMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.HAS_INVOICE];
    if (invoiceMapping) {
      const operator = focusedHasInvoice ? 'ne' : 'eq';
      params.push(buildFilterParam(invoiceMapping.entity, invoiceMapping.field, operator, 'null'));
    }
  }

  const focusedPreparation = focusedFilterValues[FILTER_NAMES.PREPARATION];
  if (typeof focusedPreparation === 'string' && focusedPreparation) {
    const preparationFieldId = PREPARATION_VALUE_TO_FIELD_ID_MAP.get(focusedPreparation);
    if (preparationFieldId) {
      params.push(buildFilterParam('checklist', 'preparationChecklistValue', 'is', 'true'));
      params.push(buildFilterParam('checklist', 'preparationChecklistFieldId', 'eq', preparationFieldId));
    }
  }

  const focusedSlaEta = focusedFilterValues[FILTER_NAMES.SLA_ETA];
  if (typeof focusedSlaEta === 'string' && focusedSlaEta) {
    if (focusedSlaEta === ETA_OVERDUE_VALUE) {
      appendEtaOverdueFilterParams(params);
    } else {
      const slaMapping = FILTER_NAME_TO_BACKEND_MAPPING[FILTER_NAMES.SLA_ETA];
      if (slaMapping) {
        params.push(buildFilterParam(slaMapping.entity, slaMapping.field, 'eq', focusedSlaEta));
        params.push(buildFilterParam(slaMapping.entity, slaMapping.field, 'not', 'null'));
      }
    }
  }

  const othersValues = normalizeFocusedValuesToStringArray(focusedFilterValues[FILTER_NAMES.OTHERS] as TRawFilterValue);
  params.push(...buildOthersFilterParams(othersValues));

  const warehouseFilter = focusedFilterValues[FILTER_NAMES.WAREHOUSE_FILTER];
  if (!!warehouseFilter) {
    params.push(`filters[${WAREHOUSE_FILTER_ACTIVE_QUERY_PATH}][$eq]=true`);
  }

  return params.join('&');
};

const buildSortQueryParam = (
  ordersFilters: ImoduleFilter[],
  sortFilter: { value: string; propertyToSort?: string } | null
): string | null => {
  const sortFilterFromOrderFilters = ordersFilters.find(filter => filter.name === FILTER_NAMES.SORT);
  const sortBy = sortFilterFromOrderFilters?.values?.[0];

  if (!sortBy) {
    return null;
  }

  const sortDirection = sortFilter?.value === 'asc' ? 'ASC' : 'DESC';

  return `sortBy=${encodeURIComponent(sortBy)}&sortDirection=${sortDirection}`;
};

/**
 * Hook personalizado que construye todos los parámetros de query para las peticiones al API.
 * Combina los valores enfocados por filtro con los filtros globales de ordersFilters.
 * Incluye el filtro del search bar según el tipo seleccionado (número de orden, empresa, etc.).
 * @param options - Opciones con focusedFilterValues, focusedOrderId, filtros, sort, datos de KAM y búsqueda
 * @returns String con todos los parámetros de query concatenados
 */
const useQueryParams = ({
  focusedFilterValues,
  focusedOrderId,
  ordersFilters,
  sortFilter,
  kamFiltersData,
  searchText,
  propertyToFilterSearch
}: IUseQueryParamsOptions): string => {
  const focusQueryParams = useMemo(
    () =>
      buildFocusQueryParams(
        focusedFilterValues,
        focusedOrderId,
        ordersFilters,
        kamFiltersData,
        searchText,
        propertyToFilterSearch
      ),
    [focusedFilterValues, focusedOrderId, ordersFilters, kamFiltersData, searchText, propertyToFilterSearch]
  );

  const sortQueryParam = useMemo(() => buildSortQueryParam(ordersFilters, sortFilter), [ordersFilters, sortFilter]);

  const allQueryParams = useMemo(() => {
    const focusHandledFilterNames: string[] = [
      FILTER_NAMES.COMPANY,
      FILTER_NAMES.COUNTRY_DESTINATION,
      FILTER_NAMES.MONTH,
      FILTER_NAMES.PAYMENT_TYPE,
      FILTER_NAMES.HAS_INVOICE,
      FILTER_NAMES.KAM,
      FILTER_NAMES.RESPONSIBLE,
      FILTER_NAMES.PREPARATION,
      FILTER_NAMES.CONFIGURATION,
      FILTER_NAMES.ORDER_TYPE,
      FILTER_NAMES.OTHERS,
      FILTER_NAMES.SLA_ETA,
      FILTER_NAMES.SORT,
      FILTER_NAMES.WAREHOUSE_FILTER
    ];

    const filtersFromOrdersFilters = ordersFilters
      .filter(filter => !focusHandledFilterNames.includes(filter.name))
      .flatMap(filter => convertFilterToParams(filter));

    const allParams: string[] = [];

    if (focusQueryParams) {
      allParams.push(focusQueryParams);
    }

    allParams.push(...filtersFromOrdersFilters);

    if (sortQueryParam) {
      allParams.push(sortQueryParam);
    }

    return allParams.join('&');
  }, [focusQueryParams, ordersFilters, sortQueryParam]);

  return allQueryParams;
};

export default useQueryParams;
